/** @format */

import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ReactComponent as Tag } from "bootstrap-icons/icons/tag-fill.svg";
import { ReactComponent as Print } from "bootstrap-icons/icons/printer.svg";
import { ReactComponent as Pencil } from "bootstrap-icons/icons/pencil.svg";
import { ReactComponent as Duplicate } from "../../../assets/icons/duplicate.svg";
import { ReactComponent as BoxArrowUpRight } from "bootstrap-icons/icons/box-arrow-up-right.svg";
import { ReactComponent as CardImage } from "bootstrap-icons/icons/card-image.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";

import SharePopup from "../../sharePopup/SharePopup";
import { useAppSelector } from "../../../app/hooks";
import {
  QualificationId,
  ResourceReference,
  UserResourceReference,
} from "../../../types";
import EditResourceRefModal from "../editUserResourceModal/EditResourceRefModal";
import ResourceLogs from "../resourceLog/ResourceLogs";
import "./ResourceFeatures.scss";
import DeleteUserResource from "../../user/deleteUserResource/DeleteUserResource";
import { useGetUserQuery } from "../../../app/services/userApi";
import { useLocale } from "../../../i18n/hooks";
import { Alert } from "react-bootstrap";

import { useGetUserResourceQuery } from "../../../app/services/userResourceApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { ErrorBoundary } from "../../errorBoundary/ErrorBoundary";
import DeleteResource from "../deleteResource/DeleteResource";
import { resourceApiSlice } from "../../../app/services/resourcesApi";
import { store } from "../../../app/store";
import { setAddNotifications } from "../../notifications/notificationsSlice";
import LoadingToast from "../../notifications/LoadingToast";

type Props = {
  resource: ResourceReference | UserResourceReference;
  authors: string[];
  fileIcon: (mimeType: string) => JSX.Element;
  preview: boolean; // true sets the component in preview mode
};

const ResourceFeatures: React.FC<Props> = ({
  resource,
  authors,
  fileIcon,
  preview,
}) => {
  const { t: _ } = useTranslation();
  const locale = useLocale();
  const resourceObject = resource.Object;
  const { userResource: _userResource } = useParams();

  const code = resource.Code;
  const copy = resource.Copy;
  const email = _userResource && (resource as UserResourceReference).User;

  const { data: userResource } = useGetUserResourceQuery(
    copy !== undefined && email
      ? { code, locale: resource.Locale, copy, email }
      : skipToken,
  );

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const { data: user, isLoading: isUserLoading } = useGetUserQuery(
    !!isAuthenticated ? undefined : skipToken,
  );
  const [resourceLink, setResourceLink] = useState<string>(
    resource.Object.links?.[0]?.src,
  );
  const [resourcePrintLink, setResourcePrintLink] = useState<string>(
    resource.Object.links?.[0]?.src,
  );
  const [isDownloadingFull, setIsDownloadingFull] = useState<boolean>(false);

  const downloadResource = async () => {
    if (user === undefined || user.role !== "admin" || isDownloadingFull)
      return;

    setIsDownloadingFull(true);
    const { data: fullResource, error: resourceError } = await store.dispatch(
      resourceApiSlice.endpoints.getFullResource.initiate({
        code: resource.Code,
      }),
    );
    const refDetails = {
      code: resource.Code,
      type: "BL",
      subjectId: resource.Subject.id,
      qualification: resource.Qualification ?? ("" as QualificationId),
    };
    const { data: enRef, error: enRefError } = await store.dispatch(
      resourceApiSlice.endpoints.getResourceReference.initiate({
        ...refDetails,
        locale: "en",
      }),
    );
    const { data: cyRef, error: cyRefError } = await store.dispatch(
      resourceApiSlice.endpoints.getResourceReference.initiate({
        ...refDetails,
        locale: "cy",
      }),
    );

    if (
      resourceError !== undefined ||
      enRefError !== undefined ||
      cyRefError !== undefined ||
      // NOTE: this is probably never true as the API should fail instead of return an empty obj.
      //       However, it works for the test so that no link needs to be created (and cause console.errors)
      Object.keys(fullResource ?? {}).length < 1
    ) {
      setIsDownloadingFull(false);
      store.dispatch(
        setAddNotifications({
          date: new Date().toISOString(),
          id: `resource-${resource.Code}-full-download`,
          title: _("downloadFullResource.title"),
          msg: `${_("downloadFullResource.fail")} ${resource.Code}`,
          type: "danger",
        }),
      );
      return;
    }

    const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
      JSON.stringify(
        { fullResource: fullResource, enRef: enRef, cyRef: cyRef },
        null,
        2,
      ),
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = `${resource.Code}-full.json`;
    link.click();
    link.remove();

    store.dispatch(
      setAddNotifications({
        date: new Date().toISOString(),
        id: `resource-${resource.Code}-full-download`,
        title: _("downloadFullResource.title"),
        msg: `${_("downloadFullResource.success")} ${resource.Code}`,
        type: "success",
      }),
    );
    setIsDownloadingFull(false);
  };

  // When the user is loaded, the link can be updated to the correct user resource link
  useEffect(() => {
    const link = resource.Object.links?.[0]?.src;
    const codeIndex = link?.toUpperCase()?.indexOf(resource.Code.toUpperCase());

    if (codeIndex === -1) {
      return;
    }
    setResourcePrintLink(
      `${link?.substring(0, codeIndex)}print/${resource.Code}`,
    );

    if (resource.Copy === undefined || user === undefined) {
      return;
    }

    setResourceLink(
      `${link?.substring(0, codeIndex)}u/${user.emailHash}/${resource.Code}/${
        resource.Copy
      }`,
    );
    // TODO: when the user resource has a print link, this can be set here
    // setResourcePrintLink(
    //   `${link.substring(0, codeIndex)}u/${user.emailHash}/${resource.Code}/${resource.Copy}`,
    // );
  }, [user, resource]);

  let filteredFiles = resourceObject?.links ? [...resourceObject.links] : [];
  filteredFiles = filteredFiles.filter(
    (file) =>
      file.label === _("Open resource") &&
      (file.category === null || file.category === ""),
  );

  return (
    <>
      {!isUserLoading && (
        <div className="resource-features" data-testid="ResourceFeatures">
          <div className="mb-3 position-relative text-center bg-secondary rounded resource-ref-thumbnail text-white align-items-center d-flex justify-content-center">
            <div
              className="h-100 w-100 rounded d-flex justify-content-center align-items-center"
              style={{
                backgroundImage: `
                    linear-gradient(0deg, rgba(25,25,25,.75) 0%,
                    rgba(25,25,25,0) 35%), 
                    url(${resource.Object?.thumbnail}) 
                  `,
                backgroundPosition: "center",
                backgroundSize: "cover",
              }}
              title={resource.Object.title ?? undefined}
            >
              {resource.Object.thumbnail ? (
                <></>
              ) : (
                <CardImage style={{ width: "40px", height: "40px" }} />
              )}
            </div>
          </div>
          {(user?.role === "wjec" ||
            user?.role === "admin" ||
            (user?.role === "teacher" && resource.Copy !== undefined)) &&
            !preview && <EditResourceRefModal resourceRef={resource} />}
          {userResource && userResource.Status !== "live" && (
            <Alert variant="warning" className="text-center mt-2 py-2">
              {_("unable-share-resource")}
            </Alert>
          )}

          <div className="resource-actions w-100 d-flex justify-content-center gap-3 my-4">
            {resource?.Type === "QFF" &&
              resource.Status === "draft" &&
              !preview && (
                <>
                  <Link
                    role="link"
                    title={_("Customise")}
                    className="action customise"
                    to={""}
                  >
                    <Pencil className="icon" />
                  </Link>
                  <Link
                    role="link"
                    title={_("Duplicate")}
                    className="action duplicate"
                    to={""}
                  >
                    <Duplicate className="icon" />
                  </Link>
                </>
              )}

            {filteredFiles.length > 0 && !preview && (
              <Link
                role="link"
                title={_("Open")}
                className="action open"
                target="_blank"
                rel="noopener noreferrer"
                to={resourceLink}
              >
                <BoxArrowUpRight className="icon" />
              </Link>
            )}

            {resource?.Type === "BL" &&
              filteredFiles.length > 0 &&
              // NOTE: this is temporary while the user resource print route doesn't exist
              resource.Copy === undefined &&
              !preview && (
                <Button
                  className="p-0 bg-transparent border-0"
                  disabled={resource.Status === "draft"}
                >
                  <Link
                    role="link"
                    title={_("Print")}
                    className="action print"
                    target="_blank"
                    rel="noopener noreferrer"
                    to={resourcePrintLink ?? ""}
                  >
                    <Print className="icon" />
                  </Link>
                </Button>
              )}
            {userResource && !preview && (
              <div className="d-flex">
                <SharePopup
                  resourceLink={resourceLink}
                  userResource={userResource}
                />
              </div>
            )}

            {resource.Type === "BL" &&
              user !== undefined &&
              user.role === "admin" && (
                <>
                  <Button
                    title={_("Download")}
                    className="action"
                    onClick={() => downloadResource()}
                  >
                    <Download className="icon" />
                  </Button>
                  <LoadingToast
                    data-testid="downloadNotif"
                    isLoading={isDownloadingFull}
                    text={_("Downloading") + "..."}
                  />
                </>
              )}

            {user &&
              _userResource &&
              user.emailHash === (resource as UserResourceReference).User &&
              !preview && (
                <DeleteUserResource
                  resource={resource as UserResourceReference}
                />
              )}

            {resource?.Type === "BL" && !preview && user?.role === "admin" && (
              <DeleteResource resource={resource} />
            )}
          </div>

          {_userResource === undefined ? (
            <>
              <div className="mb-3 d-flex justify-content-center gap-3 authors">
                <strong>
                  {_("Authors", { count: authors?.length ?? 0 })}:{" "}
                </strong>
                <div className="d-flex flex-wrap">
                  {authors[0]?.length
                    ? resourceObject.authors
                    : _("No authors provided")}
                </div>
              </div>
              <div
                className="
                  acknowledgements 
                  mb-3 
                  d-flex 
                  flex-column
                  flex-sm-row
                  align-items-center
                  align-items-sm-start 
                  justify-content-center 
                  gap-1
                "
              >
                <strong>{_("Acknowledgements")}:</strong>

                {!resourceObject.acknowledgements ||
                resourceObject.acknowledgements?.length === 0 ? (
                  <div className="d-flex flex-wrap">
                    {_("No acknowledgements provided")}
                  </div>
                ) : (
                  <ul className="list-unstyled mb-0">
                    {resourceObject.acknowledgements?.length > 0 &&
                    Array.isArray(resourceObject.acknowledgements) ? (
                      resourceObject.acknowledgements.map((file) => (
                        <li key={file.src + file.label}>
                          <a
                            title={file.label}
                            className="btn btn-light py-0"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={file.src}
                            download
                          >
                            {fileIcon(file.mimeType)} {file.label}{" "}
                            <small className="fs-6 text-muted">
                              <BoxArrowUpRight />
                            </small>
                          </a>
                        </li>
                      ))
                    ) : (
                      <li>
                        <a
                          title={_("Acknowledgements")}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-light py-0"
                          href={resourceObject.acknowledgements.toString()}
                          download
                        >
                          {fileIcon("pdf")} {_("Acknowledgements")}{" "}
                          <small className="fs-6 text-muted">
                            <BoxArrowUpRight />
                          </small>
                        </a>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </>
          ) : (
            resource.Code &&
            resource.Type &&
            resource.Qualification &&
            resource.Subject?.title?.[locale] && (
              <div className="tags mb-3 d-flex justify-content-center gap-3">
                <Link
                  role="link"
                  to={`/${locale}/${resource.Subject.title[locale]}/r/${resource.Code}/${resource.Type}/${resource.Qualification}`}
                >
                  {_("Original resource")}{" "}
                </Link>
              </div>
            )
          )}

          <div className="tags mb-3 d-flex justify-content-center gap-3">
            <strong>{_("Tags")}:</strong>
            <div className="d-flex flex-wrap">
              {resourceObject.tags?.length > 0
                ? resourceObject.tags.map(
                    (_tag: string, index: number) =>
                      _tag && (
                        <Link
                          role="link"
                          title={_tag}
                          key={_tag + index}
                          className="btn rounded-pill p-0 me-2 text-decoration-none btn-link"
                          to={preview ? "" : `../?tags=${_tag}`}
                        >
                          <Tag />
                          &thinsp;{_tag}
                        </Link>
                      ),
                  )
                : _("No tags provided")}
            </div>
          </div>

          {!preview && (
            <ErrorBoundary
              fallback={
                user?.role === "wjec" ? (
                  <Alert variant="danger">
                    {_("error-boundary.failed-load-logs")}
                  </Alert>
                ) : (
                  <></>
                )
              }
            >
              <ResourceLogs resource={resource as ResourceReference} />
            </ErrorBoundary>
          )}
        </div>
      )}
    </>
  );
};

export default ResourceFeatures;
